import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CurrencyPipe } from '../../../../pipes/currency.pipe';
import { DatePipe } from '../../../../pipes/date.pipe';
import { NumberPipe } from '../../../../pipes/number.pipe';
import { DetailedQuote } from '../../../../domain/quote.domain';
import { PlotData, QuoteIssueType, QuotePriceType } from '../domain';
import { DateTimeFormat, DateTimeFormatType } from '../../../../services/date/date-formatter.service';
import type { Point } from 'highcharts';

interface AxisLabelContext {
  value: number | string;
}

interface TooltipPointContext {
  point: {
    y: number;
    open?: number;
    high?: number;
    low?: number;
    close?: number;
  };
}

interface TooltipContext {
  x: number;
  y: number;
  points?: Array<TooltipPointContext>;
}

@Injectable({
  providedIn: 'root'
})
export class DetailedChartFormatterService {
  private readonly translateService = inject(TranslateService);
  private readonly currencyPipe = inject(CurrencyPipe);
  private readonly datePipe = inject(DatePipe);
  private readonly numberPipe = inject(NumberPipe);

  /**
   * Determine the number based on value and if its penny stock
   * Default is 2
   *
   * @param value Value
   * @param isPennyStock
   * @returns no of decimal places
   */
  static decimalPlaces(value: number, isPennyStock: boolean): number {
    if (value > 1000) {
      return 0;
    } else if (isPennyStock) {
      return 4;
    }
    return 2;
  }

  /**
   * Return call-back function for XAxis label with formatted dates
   *
   * @param formatKey
   * @param includedYear
   * @returns call back function for label formatter
   */
  createXAxisLabelFormatter(formatKey: DateTimeFormatType, includedYear: boolean): () => string {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self: DetailedChartFormatterService = this;
    const labelFormatter: () => string =  function(this: AxisLabelContext): string {
      const labelDate = new Date(this.value);
      if (includedYear) {
        return self.datePipe.transform(labelDate, { format: DateTimeFormat.MONTH }) + '<br>' +
          self.datePipe.transform(labelDate, { format: formatKey } );
      }
      return self.datePipe.transform(labelDate, { format: formatKey } );
    };
    return labelFormatter;
  }

  /**
   * Creates tooltip formatter call back function
   *
   * @param isPennyStock
   * @param showOnlyDate
   * @returns for getting formatted html for tooltip
   */
  createTooltipFormatter(isPennyStock: boolean, showOnlyDate: boolean): () => string {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self: DetailedChartFormatterService = this;
    const tooltipFormatter: () => string = function(this: TooltipContext): string {
      const decimalPlaces = DetailedChartFormatterService.decimalPlaces(this.y, isPennyStock);
      let tooltip: string;
      let timestamp: string;
      if (showOnlyDate) {
        timestamp = self.datePipe.transform(new Date(this.x));
      } else {
        timestamp = self.datePipe.transform(new Date(this.x), { format: DateTimeFormat.DATE_TIME });
      }
      if (this.points !== undefined && this.points[0]?.point?.close !== undefined && this.points[1]?.point !== undefined) {
        const volumeValue = self.tooltipSeriesFormatter('chartConfig.VOLUME', this.points[1].point.y, 0);
        const point = this.points[0].point;
        const seriesOpen = self.tooltipSeriesFormatter('chartConfig.CANDLE_OPEN', point.open, decimalPlaces);
        const seriesHigh = self.tooltipSeriesFormatter('chartConfig.CANDLE_HIGH', point.high, decimalPlaces);
        const seriesLow = self.tooltipSeriesFormatter('chartConfig.CANDLE_LOW', point.low, decimalPlaces);
        const seriesClose = self.tooltipSeriesFormatter('chartConfig.CANDLE_CLOSE', point.close, decimalPlaces);

        tooltip = `<span style="font-family: Roboto, Helvetica, sans; font-size: 13px">
            <span style="z-index: 10;">${timestamp}</span>
            <br>${seriesOpen}
            <br>${seriesHigh}
            <br>${seriesLow}
            <br>${seriesClose}
            <br>${volumeValue}
            <span>`;
      } else {
        const seriesValue = showOnlyDate ? self.tooltipSeriesFormatter('CLOSE', this.y, decimalPlaces) :
          self.tooltipSeriesFormatter('PRICE', this.y, decimalPlaces);
        tooltip = `<span style="font-family: Roboto, Helvetica, sans; font-size: 13px">
              ${timestamp}
              <br>${seriesValue}`;
        if (this.points !== undefined && this.points[1] !== undefined) {
          const volumeValue = self.tooltipSeriesFormatter('chartConfig.VOLUME', this.points[1].point.y, 0);
          tooltip += `<br>${volumeValue}`;
        }
        tooltip += '</span>';
      }
      return tooltip;
    };
    return tooltipFormatter;
  }

  /**
   * Creates label formatter call back function
   *
   * @returns for getting formatted label text
   */
  getTimeSeriesFormatter(): () => string {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;
    const labelFormatter: () => string = function(this: AxisLabelContext): string {
      return self.numberPipe.transform(Number(this.value), { shorten: true });
    };
    return labelFormatter;
  }

  /**
   * Given a quote, return an object which contains a price and a display name
   * for a line to be plotted on a chart.
   * The type of price return is dependent on the quote's IssueType. I.e an option will
   * return a strike price as the price value.
   *
   * @param quote
   * @returns
   */
  getLinePlotData(quote: DetailedQuote): PlotData {
    let value;
    let displayValue;
    let displayName;
    switch (quote.issueType) {
      case QuoteIssueType.INDEX:
        value = quote.previousClose;
        displayValue = this.numberPipe.transform(value, { format: 'n0' });
        displayName = QuotePriceType.PREVIOUS_CLOSE;
        break;

      case QuoteIssueType.OPTION:
        value = quote.strikePrice;
        displayValue = this.currencyPipe.transform(value);
        displayName = QuotePriceType.STRIKE_PRICE;
        break;

      case QuoteIssueType.STOCK:
        value = quote.previousClose;
        displayValue = this.currencyPipe.transform(value,
          { format: 'c' + DetailedChartFormatterService.decimalPlaces(value, quote.isPennyStock) });
        displayName = QuotePriceType.PREVIOUS_CLOSE;
        break;

      default:
        value = quote.previousClose;
        displayValue = this.currencyPipe.transform(value);
        displayName = QuotePriceType.PREVIOUS_CLOSE;
    }
    return { value, displayValue, displayName };
  }

  /**
   * Format tooltip series point value
   *
   * @param seriesName
   * @param point
   * @param decimalPlaces
   * @returns
   */
  private tooltipSeriesFormatter(seriesName: string, point: number, decimalPlaces: number): string {
    let series = this.translateService.instant('tdWb.markets.quote.' + seriesName) + ': ';
    series += this.numberPipe.transform(point, { format: 'n' +  decimalPlaces });
    return series;
  }

  createPointDescriptionFormatter(showOnlyDate: boolean): (point: Point) => string {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self: DetailedChartFormatterService = this;
    const tooltipFormatter: (point: Point) => string = function(point: Point): string {
      let timestamp: string;
      if (showOnlyDate) {
        timestamp = self.datePipe.transform(new Date(point.x));
      } else {
        timestamp = self.datePipe.transform(new Date(point.x), { format: DateTimeFormat.DATE_TIME });
      }
      if (point.series.name === 'Volume') {
        const volumeValue = self.tooltipSeriesFormatter('chartConfig.VOLUME', point.y, 0);
        return `${timestamp} ${volumeValue}`;
      } else {
        const seriesValue = showOnlyDate ? self.tooltipSeriesFormatter('CLOSE', point.y, 2) :
          self.tooltipSeriesFormatter('PRICE', point.y, 2);
        return `${timestamp} ${seriesValue}`;
      }

    };
    return tooltipFormatter;
  }
}
